import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Theaters as TheaterIcon,
  Info as InfoIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useVenuesApi, useStagesApi } from "@/context/AppContext";
import { ResponsiveCardGrid } from "@/components/ResponsiveCardGrid";
import type { VenueList as VenueListType, Stage } from "api-client/models";

interface VenueWithStages extends VenueListType {
  stages?: Stage[];
  totalCapacity?: number;
}

export const VenueList: React.FC = () => {
  const [venues, setVenues] = useState<VenueWithStages[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const venuesApi = useVenuesApi();
  const stagesApi = useStagesApi();

  useEffect(() => {
    fetchVenues();

    // Check for navigation state message
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const response = await venuesApi.venuesList();
      const venuesData = response.results || [];

      // Fetch all stages once
      const allStagesResponse = await stagesApi.stagesList();
      const allStages = allStagesResponse.results || [];

      // Group stages by venue and calculate capacities
      const venuesWithStages = venuesData.map((venue: VenueListType) => {
        const stages = allStages.filter(
          (stage: Stage) => stage.venue === venue.id,
        );
        const totalCapacity = stages.reduce(
          (sum: number, stage: Stage) => sum + (stage.capacity || 0),
          0,
        );

        return {
          ...venue,
          stages,
          totalCapacity,
        };
      });

      setVenues(venuesWithStages);
      setError(null);
    } catch {
      setError("Failed to load venues. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (venueId: string) => {
    if (!confirm("Are you sure you want to delete this venue?")) {
      return;
    }

    try {
      await venuesApi.venuesDestroy({ id: venueId });
      // Refresh the list
      fetchVenues();
    } catch {
      alert("Failed to delete venue. Please try again.");
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading venues...</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Venues
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your performance venues and stages
          </Typography>
          {venues.length >= 1 && (
            <Alert severity="info" sx={{ mt: 1, mb: 0 }}>
              Need additional venues? Contact Solid Slate to add more venues to
              your team.
            </Alert>
          )}
        </Box>
        {venues.length < 1 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/venues/new")}
          >
            New Venue
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => setInfoMessage(null)}
        >
          {infoMessage}
        </Alert>
      )}

      {venues.length === 0 ? (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <TheaterIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                No venues yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get started by creating your first venue
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/venues/new")}
              >
                Create Venue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <ResponsiveCardGrid>
          {venues.map((venue) => (
            <Card
              key={venue.id}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="start"
                    spacing={1}
                  >
                    <Box
                      sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {venue.name}
                      </Typography>
                      <Chip
                        label={venue.venueType}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    </Box>
                    <Chip
                      label={venue.isActive ? "Active" : "Inactive"}
                      color={venue.isActive ? "success" : "default"}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TheaterIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {venue.stagesCount} stage
                      {Number(venue.stagesCount) !== 1 ? "s" : ""}
                    </Typography>
                    {Number(venue.stagesCount) === 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          navigate(`/stages/new?venue=${venue.id}`)
                        }
                        sx={{ ml: 1 }}
                      >
                        Add Stage
                      </Button>
                    )}
                  </Stack>

                  {Number(venue.stagesCount) > 0 && (
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PeopleIcon fontSize="small" color="action" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="medium"
                        >
                          Total capacity:{" "}
                          {venue.totalCapacity?.toLocaleString() || 0}
                        </Typography>
                      </Stack>
                      {venue.stages && venue.stages.length > 0 && (
                        <Box sx={{ pl: 4 }}>
                          <Stack spacing={0.5}>
                            {venue.stages.map((stage) => (
                              <Typography
                                key={stage.id}
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                • {stage.name}:{" "}
                                {stage.capacity?.toLocaleString() || 0}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  )}

                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {venue.teamName}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  flexWrap: "wrap",
                  gap: 1,
                  "& > button": { minHeight: 44, minWidth: 44 },
                }}
              >
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => navigate(`/venues/${venue.id}`)}
                >
                  Details
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/venues/${venue.id}/edit`)}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(venue.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </ResponsiveCardGrid>
      )}
    </Box>
  );
};
